import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../lib/utils";
import { handwritten } from "./editorial";

const faqs = [
  {
    question: "What's a loop?",
    answer:
      "Anything you do with other people. A coffee, a run, a walk, a picnic, a pub night, a book club. If it's a real-world plan, it's a loop.",
  },
  {
    question: "How does it know what to show me?",
    answer:
      "Your Home feed mixes loops from communities you've joined, interests you've picked, and people you've previously looped with. Your Map view shows whatever's nearby right now.",
  },
  {
    question: "Is it actually free?",
    answer:
      "Yes, forever. No subscription, no premium tier, no paywall. We'll fund it later with ads, event sponsorships, and optional in-app extras, none of which gate the core product.",
  },
  {
    question: "Where does it work?",
    answer:
      "London first, then expanding. We seed each new area with real, recurring loops before launching so the feed isn't empty when you arrive.",
  },
  {
    question: "Is it safe?",
    answer:
      "Yes. Profiles are real-name and photo-first, never anonymous. You see hosts' history and attendance before joining. Privacy zones hide your home and work. You can mute, block, and report in one tap. Per-loop filters (age range, friends-of-friends) are organiser-set.",
  },
  {
    question: "Can I post my own?",
    answer:
      "Yes. Three taps to create. No event-management overhead. People you'd want there see it; people you wouldn't, don't.",
  },
];

const FAQ: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  return (
    <section className="py-24 px-6 md:px-12 bg-paper-tan">
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24">
        {/* Header */}
        <div className="lg:col-span-5">
          <div className="flex items-center gap-4 mb-6">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-salmon whitespace-nowrap">
              05: Questions
            </span>
            <span className="flex-1 h-px bg-ink/15" />
          </div>
          <p
            className="text-2xl md:text-3xl text-salmon -rotate-1 mb-2"
            style={handwritten}
          >
            ask away
          </p>
          <h2 className="text-4xl md:text-6xl font-serif tracking-tight mb-6 leading-[1.05]">
            Everything you <br />{" "}
            <span className="italic text-salmon">need to know.</span>
          </h2>
          <p className="text-lg text-gray-700 mb-8 max-w-md">
            Spontaneous community sounds like a contradiction. Here&rsquo;s how
            it actually works, what&rsquo;s real about it, and why it&rsquo;s
            free.
          </p>
          <a
            href="https://www.instagram.com/letsloop.app"
            target="_blank"
            rel="noopener noreferrer"
            className="text-salmon font-semibold border-b-2 border-salmon/30 hover:border-salmon transition-colors"
          >
            Find us on Instagram &rarr;
          </a>
        </div>

        {/* Accordion */}
        <div className="lg:col-span-7 flex flex-col">
          {faqs.map((faq, index) => (
            <AccordionItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={activeIndex === index}
              onClick={() =>
                setActiveIndex(activeIndex === index ? null : index)
              }
            />
          ))}
        </div>
      </div>
    </section>
  );
};

interface AccordionItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onClick: () => void;
}

const AccordionItem: React.FC<AccordionItemProps> = ({
  question,
  answer,
  isOpen,
  onClick,
}) => {
  return (
    <div className="border-b-2 border-ink/10">
      <button
        onClick={onClick}
        className="w-full py-8 flex items-center justify-between text-left group transition-colors hover:text-salmon"
      >
        <span
          className={cn(
            "font-serif text-xl md:text-2xl tracking-tight pr-8 transition-colors",
            isOpen ? "text-salmon" : "text-ink",
          )}
        >
          {question}
        </span>
        <span
          className={cn(
            "text-3xl text-salmon shrink-0 transition-transform duration-300 leading-none",
            isOpen ? "rotate-45" : "",
          )}
        >
          +
        </span>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <p className="pb-8 text-lg text-gray-600 leading-relaxed max-w-2xl">
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FAQ;
